import {
  Badge,
  Button,
  DataTable,
  FlexBox,
  IconButton,
  Page,
  Text,
} from '@dixa/lib-shared-habla';
import { client as elevioClient } from 'elevio';
import { useHasAccess } from 'lib-shared-access-control';
import { useCallback, useMemo } from 'react';

import { aiApi } from 'apis/ai';
import { AiStyleGuide } from 'apis/ai/types/aiStyleGuides.types';
import { appActions } from 'app/app.actions';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { getChannelName } from 'utils/channels';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppNavigate } from 'utils/url/useAppNavigate';

import styles from './settingsAIOptionsStyleGuide.module.scss';
export const SettingsAIOptionsStyleGuideView = (): JSX.Element => {
  const navigate = useAppNavigate();
  const dispatch = useAppDispatch();
  const notify = useNotifications();

  const hasManageCopilotAccess = useHasAccess('settings.copilot', {
    fallbackRole: 'systemadmin',
  });

  const { isLoading, isError, styleGuides, refetch } =
    aiApi.useReadAIWritingStyleGuidesQuery(undefined, {
      selectFromResult: ({ data, ...rest }) => ({
        ...rest,
        styleGuides: data?.data.style_guides,
      }),
    });

  const [deleteStyleguide] = aiApi.useDeleteAIWritingStyleGuideMutation();

  const emptyStateProps = useMemo(() => {
    return styleGuides?.length === 0
      ? {
          title: 'No writing style guides found',
          description: 'Click "Add writing style guide" in order to add one',
        }
      : undefined;
  }, [styleGuides]);

  const handleEdit = (styleguide: AiStyleGuide) => {
    navigate({
      path: `/settings/ai/style-guide/:styleguideId`,
      params: {
        path: {
          styleguideId: styleguide.id,
        },
      },
    });
  };

  const handleRemove = async (styleguide: AiStyleGuide) => {
    const { isConfirmed, confirm } = await dispatch(
      appActions.showModal({
        modal: {
          type: 'app:confirm',
          arg: {
            title: `Delete "${styleguide.name}"`,
            description:
              'Are you sure you want to delete your writing style guide?',
            confirm: {
              label: 'Delete',
              async: true,
              asyncLabel: 'Deleting...',
            },
          },
        },
      }),
    );

    if (isConfirmed) {
      try {
        confirm(async () => {
          await deleteStyleguide({
            styleguideId: styleguide.id,
          }).unwrap();

          notify(
            {
              title: 'Writing style guide successfully deleted.',
              time: 3000,
            },
            NotificationType.Success,
          );
        });
      } catch {
        notify(
          {
            title: 'An error occurred while deleting writing style guide.',
          },
          NotificationType.Error,
        );
      }
    }
  };

  const showFeedbackModal = useCallback(() => {
    dispatch(
      appActions.showModal({
        modal: {
          type: 'app:feedbackForm',
          arg: {
            team: 'conversations',
            title: 'Writing style guide feedback',
          },
        },
      }),
    );
  }, [dispatch]);

  const showElevioArticle = useCallback(() => {
    elevioClient.openArticle('1769');
  }, []);

  return (
    <Page>
      <Page.Head title="Writing style" />

      <Page.Body>
        <Page.Section
          title="All writing style guides"
          titleMeta={
            <>
              <IconButton
                icon="question"
                variant="tertiary"
                onClick={showElevioArticle}
              />
              <Text.ExternalLink
                onClick={showFeedbackModal}
                className={styles.feedbackLink}
              >
                Send feedback
              </Text.ExternalLink>
            </>
          }
          controls={
            hasManageCopilotAccess ? (
              <Button
                onClick={() => {
                  navigate({
                    path: '/settings/ai/style-guide/create',
                  });
                }}
              >
                Add writing style guide
              </Button>
            ) : undefined
          }
        >
          <DataTable
            columns={[
              {
                label: 'Name',
                selector: ({ name }) => name,
              },
              {
                label: 'Connected to',
                selector: (styleGuide) => {
                  const channels = styleGuide.contexts.channels ?? [];
                  return (
                    <FlexBox gap="200">
                      {channels.map((channel, index) => {
                        return (
                          <Badge key={index} variant="info" size="small">
                            {getChannelName(channel)}
                          </Badge>
                        );
                      })}
                    </FlexBox>
                  );
                },
              },
              {
                label: '',
                selector: (styleguide) => (
                  <FlexBox justifyContent="flex-end">
                    <IconButton
                      variant="tertiary"
                      icon="pen"
                      onClick={() => handleEdit(styleguide)}
                    />

                    <IconButton
                      variant="tertiary"
                      icon="trash"
                      onClick={() => handleRemove(styleguide)}
                    />
                  </FlexBox>
                ),
                style: {
                  width: 0,
                },
              },
            ]}
            data={styleGuides ?? []}
            tableStatusProps={{
              loading: isLoading,
              loadingFailed: isError,
              onLoadingRetry: refetch,
              emptyState: emptyStateProps,
            }}
          ></DataTable>
        </Page.Section>
      </Page.Body>
    </Page>
  );
};
